import { useContext, useMemo } from "react";
import { useLocation } from "react-router";

import { getModuleMetadata } from "../api";
import { ShellContext } from "../context";
import { useModules, usePermissionCheck } from "../hooks";
import { PAGE_AGENT_CHAT_OPT_IN_KEY, useAgentChatPageContext } from "./AgentChatContext";

/**
 * Permission the shell needs before it can hold a conversation at all. Deliberately does not
 * include `ai_streaming:jarvis_get_chat_history`: replaying an earlier conversation is a bonus,
 * and the flyout already falls back to a fresh session when that call is refused.
 *
 * TODO(IAM): `ai_streaming:jarvis_get_chat_history` is granted by the
 * `ai-jarvis-judi-bot-ai-streaming-access` policy (see
 * `iam.capitalrx.com/backend/alembic_iam/versions/e05294ab81f2_update_ai_judi_bot_policy.py`), but
 * that policy is only attached to the `ai-jarvis-judi-bot` group. Confirm the front-end user groups
 * that should see the chatbot are attached to it before this ships beyond the hostnames in
 * `ENABLED_HOSTNAME_FRAGMENTS`; until then, history replay 403s while sending still works.
 */
const REQUIRED_PERMISSIONS: ReadonlyArray<string> = ["ai_streaming:jarvis_send_message"];

/** Hostname fragments where the agent chatbot is currently enabled. */
const ENABLED_HOSTNAME_FRAGMENTS: ReadonlyArray<string> = ["staging", "qa", "demo", "localhost", "10.211.55.3"];

const NO_PERMISSIONS: ReadonlyArray<string> = [];

/** Resolves the configured Jarvis streaming base url for the current page. */
export const useAgentChatBaseUrl = (): string => {
  return useMemo(() => {
    const template = typeof site === "undefined" ? "" : site.aiStreamingBaseUrl ?? "";
    return template
      .replace("{{protocol}}", window.location.protocol)
      .replace("{{hostname}}", window.location.hostname);
  }, []);
};

/**
 * Returns the id of the module owning the current route, or `null` outside any module. The chatbot
 * is mounted above the module routes, so it cannot read `OwnerModuleContext` and resolves the
 * active module from the path instead.
 */
const useActiveModuleId = (): string | null => {
  const { moduleRootPath } = useContext(ShellContext);
  const modules = useModules();
  const { pathname } = useLocation();

  return useMemo(() => {
    const rootPath = moduleRootPath === "/" ? "" : moduleRootPath;

    for (const module of modules) {
      const modulePath = `${rootPath}/${module.info.id}`;
      if (pathname === modulePath || pathname.startsWith(`${modulePath}/`)) return module.info.id;
    }

    return null;
  }, [moduleRootPath, modules, pathname]);
};

/**
 * Indicates whether the agent chatbot is available to the current user on the current host and in
 * the current module.
 *
 * A module opts in through `features.agentChat.enabled` in its `metadata.json`, and may name extra
 * permissions its users must hold on top of the shell's baseline. Modules that say nothing never
 * show the chatbot, so no page can leak its content to the agent by accident.
 *
 * A single page inside a module that has not opted in can turn the chatbot on for itself by
 * publishing `PAGE_AGENT_CHAT_OPT_IN_KEY` through `useAgentChatContext`. The opt-in lasts only as
 * long as that page is mounted, and the host and permission checks below still apply. A module that
 * sets `enabled: false` has refused the chatbot outright, and no page inside it can override that.
 *
 * Pages should gate any work done purely to feed `useAgentChatContext` on this hook, so that
 * environments, modules, and users without the chatbot don't pay for context they will never send.
 */
export const useIsAgentChatEnabled = (): boolean => {
  const activeModuleId = useActiveModuleId();
  const baseUrl = useAgentChatBaseUrl();

  const agentChat = activeModuleId ? getModuleMetadata(activeModuleId)?.features?.agentChat : undefined;
  const isModuleEnabled = agentChat?.enabled === true;
  const isModuleOptedOut = agentChat?.enabled === false;

  const pageContext = useAgentChatPageContext();
  const isPageEnabled = !isModuleOptedOut && pageContext[PAGE_AGENT_CHAT_OPT_IN_KEY] === true;

  const requiredPermissions = useMemo(
    () => [...REQUIRED_PERMISSIONS, ...(agentChat?.permissions ?? NO_PERMISSIONS)],
    [agentChat],
  );

  const hasRequiredPermissions = usePermissionCheck(requiredPermissions);

  const isDocumentationChatbotDemoPage = window.location.pathname.includes("/module/documentation/agent-chatbot");
  if (isDocumentationChatbotDemoPage) {
    return false;
  }

  const isEnabledHostname = ENABLED_HOSTNAME_FRAGMENTS.some(
    (fragment) => window.location.host.includes(fragment),
  );

  return (isModuleEnabled || isPageEnabled) && hasRequiredPermissions && isEnabledHostname && Boolean(baseUrl);
};
